import React, { useState, useEffect } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db, auth } from "../../firebase-config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../assets/styles/ClientShared.css";

const CustomDomainSetup = () => {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [existingDomain, setExistingDomain] = useState("");

  useEffect(() => {
    const fetchExistingDomain = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("User not authenticated.");

        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) throw new Error("User document not found.");

        const userData = userSnap.data();
        if (!userData.projectId) throw new Error("Project ID not found.");

        const projectRef = doc(db, "projects", userData.projectId);
        const projectSnap = await getDoc(projectRef);

        if (projectSnap.exists()) {
          const projectData = projectSnap.data();
          setExistingDomain(projectData.customDomain || "");
          setDomain(projectData.customDomain || "");
        }
      } catch (error) {
        console.error("Error fetching domain:", error.message);
        toast.error("Failed to load domain information. Please try again.");
      }
    };

    fetchExistingDomain();
  }, []);

  const handleDomainSubmit = async () => {
    if (!domain) {
      toast.error("Please enter your custom domain.");
      return;
    }

    setLoading(true);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("User not authenticated.");

      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) throw new Error("User document not found.");

      const userData = userSnap.data();
      if (!userData.projectId) throw new Error("Project ID not found.");

      const projectRef = doc(db, "projects", userData.projectId);
      await updateDoc(projectRef, { customDomain: domain });

      toast.success("Domain submitted successfully! We'll configure it shortly.");
      setExistingDomain(domain);
    } catch (error) {
      console.error("Error submitting domain:", error.message);
      toast.error("Failed to submit domain. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cp-page cp-page--narrow">
      <ToastContainer />
      <div className="cp-page-header">
        <p className="cp-eyebrow">Client Portal</p>
        <h1 className="cp-title">Custom Domain Setup</h1>
        <p className="cp-subtitle">Submit your custom domain and we'll configure it for you.</p>
      </div>

      {/* Instructions */}
      <div className="cp-section" style={{marginBottom:'20px'}}>
        <div className="cp-section-header"><h2 className="cp-section-title">Instructions</h2></div>
        <div className="cp-section-body">
          <p style={{fontFamily:'var(--font-ui)',fontSize:'0.9375rem',color:'var(--text-secondary)',lineHeight:1.7,marginTop:0}}>
            Purchase a domain from <a href="https://www.godaddy.com" target="_blank" rel="noopener noreferrer" className="cp-doc-link">GoDaddy</a> or a registrar of your choice. Then enter your domain below — we'll configure it on Firebase for you.
          </p>
          <ol style={{fontFamily:'var(--font-ui)',fontSize:'0.9375rem',color:'var(--text-secondary)',lineHeight:1.8,paddingLeft:'20px',margin:'12px 0 0'}}>
            <li>Purchase your domain (GoDaddy, Namecheap, Google Domains, etc.)</li>
            <li>Note the domain name (e.g., www.example.com)</li>
            <li>Enter it below and click Submit</li>
          </ol>
        </div>
      </div>

      {/* Submit form */}
      <div className="cp-section" style={{marginBottom:'24px'}}>
        <div className="cp-section-header">
          <h2 className="cp-section-title">Submit Your Domain</h2>
          {existingDomain && <span style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',color:'var(--text-tertiary)'}}>Current: {existingDomain}</span>}
        </div>
        <div className="cp-section-body">
          <div className="cp-form-group">
            <label htmlFor="domain">Your Custom Domain</label>
            <input type="text" id="domain" placeholder="e.g. example.com" value={domain} onChange={e=>setDomain(e.target.value)} />
          </div>
          <div className="cp-btn-row">
            <button className="cp-btn-primary" onClick={handleDomainSubmit} disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Domain'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomDomainSetup;
